import React, { useEffect, useState } from 'react'
import { Button, Segmented, Table, Tag, Tooltip, Typography } from 'antd'
import { ColumnsType } from 'antd/es/table'
import {
  CopyOutlined,
  EditOutlined,
  InboxOutlined,
  PlusOutlined,
  UndoOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'
import { actionsApi } from 'api/actions'
import { IAdLink } from 'models/adLinks'
import { superAdminRoutes } from 'shared/constants/routes'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import { AdLinkModal } from './AdLinkModal'
import { adLinkTypeColors, adLinkTypeLabels } from './adLinkTypes'
import styles from './AdLinks.module.scss'

type TAdLinksTab = 'active' | 'archived'

export const ActionsAdLinks: React.FC = () => {
  const [links, setLinks] = useState<IAdLink[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [tab, setTab] = useState<TAdLinksTab>('active')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editedLink, setEditedLink] = useState<IAdLink | null>(null)
  const [archivingId, setArchivingId] = useState<string | null>(null)

  useEffect(() => {
    actionsApi
      .getAdLinks()
      .then((response) => setLinks(response.data))
      .catch(showServerError)
      .finally(() => setIsLoading(false))
  }, [])

  const activeLinks = links.filter((link) => !link.archived)
  const archivedLinks = links.filter((link) => link.archived)

  const openModal = (link: IAdLink | null) => {
    setEditedLink(link)
    setIsModalOpen(true)
  }

  const handleCopy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      showMessage('success', 'Ссылка скопирована', '')
    } catch {
      showMessage('error', 'Не удалось скопировать ссылку', '')
    }
  }

  const replaceLink = (saved: IAdLink) =>
    setLinks((prev) =>
      prev.some((link) => link._id === saved._id)
        ? prev.map((link) => (link._id === saved._id ? saved : link))
        : [saved, ...prev]
    )

  const onSaved = (saved: IAdLink) => {
    replaceLink(saved)
    setIsModalOpen(false)
    if (!editedLink) {
      setTab('active')
      handleCopy(saved.url)
    }
  }

  const toggleArchive = async (link: IAdLink) => {
    try {
      setArchivingId(link._id)
      const { data } = await actionsApi.updateAdLink(link._id, { archived: !link.archived })
      replaceLink(data)
      showMessage(
        'success',
        data.archived ? 'Ссылка в архиве' : 'Ссылка возвращена из архива',
        data.archived ? 'Переходы по ней продолжают засчитываться' : ''
      )
    } catch (e) {
      showServerError(e)
    } finally {
      setArchivingId(null)
    }
  }

  const columns: ColumnsType<IAdLink> = [
    {
      title: 'Название',
      dataIndex: 'name',
      width: 300,
      render: (name: string, link) => (
        <span className={styles.name}>
          <Tag color={adLinkTypeColors[link.type]}>{adLinkTypeLabels[link.type]}</Tag>
          {name}
        </span>
      )
    },
    {
      title: 'Ссылка',
      dataIndex: 'url',
      render: (url: string) => (
        <span className={styles.url}>
          <Typography.Text className={styles.urlText} ellipsis={{ tooltip: url }}>
            {url}
          </Typography.Text>
          <Tooltip title='Скопировать'>
            <Button
              type='text'
              size='small'
              icon={<CopyOutlined />}
              aria-label='Скопировать ссылку'
              onClick={() => handleCopy(url)}
            />
          </Tooltip>
        </span>
      )
    },
    {
      title: 'Создана',
      dataIndex: 'createdAt',
      width: 110,
      render: (createdAt: string) => dayjs(createdAt).format('DD.MM.YYYY')
    },
    {
      key: 'actions',
      width: 230,
      align: 'right',
      render: (_, link) => (
        <span className={styles.actions}>
          <Button size='small' icon={<EditOutlined />} onClick={() => openModal(link)}>
            Изменить
          </Button>
          <Button
            size='small'
            icon={link.archived ? <UndoOutlined /> : <InboxOutlined />}
            loading={archivingId === link._id}
            onClick={() => toggleArchive(link)}
          >
            {link.archived ? 'Вернуть' : 'В архив'}
          </Button>
        </span>
      )
    }
  ]

  return (
    <div className={styles.root}>
      <div className={styles.titleRow}>
        <Typography.Title level={4} className={styles.title}>
          Рекламные ссылки
        </Typography.Title>
        <Button icon={<PlusOutlined />} onClick={() => openModal(null)}>
          Создать ссылку
        </Button>
      </div>
      <Typography.Paragraph className={styles.hint}>
        Ссылка ведёт на страницу сайта с метками. Переходы по ней видны в{' '}
        <Link to={`${superAdminRoutes.ACTIONS}/${superAdminRoutes.ACTIONS_ANALYTICS}`}>
          сквозной аналитике
        </Link>
        : в источниках и в отдельном блоке «Рекламные ссылки». Ссылка из архива
        продолжает работать и засчитываться.
      </Typography.Paragraph>
      <Segmented
        value={tab}
        onChange={(value) => setTab(value as TAdLinksTab)}
        options={[
          { value: 'active', label: `Активные (${activeLinks.length})` },
          { value: 'archived', label: `Архив (${archivedLinks.length})` }
        ]}
        className={styles.tabs}
      />
      <Table
        rowKey='_id'
        size='small'
        loading={isLoading}
        columns={columns}
        dataSource={tab === 'active' ? activeLinks : archivedLinks}
        pagination={{ pageSize: 20, showSizeChanger: false, hideOnSinglePage: true }}
        locale={{ emptyText: tab === 'active' ? 'Ссылок пока нет' : 'В архиве пусто' }}
      />
      <AdLinkModal
        open={isModalOpen}
        link={editedLink}
        onClose={() => setIsModalOpen(false)}
        onSaved={onSaved}
      />
    </div>
  )
}
